import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { UserCheck, Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';

export default function CoachRegisterPage() {
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', confirm_password: '', phone: '', company: 'Full Arch Buying Group'
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.full_name || !form.email || !form.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/public/coach-register`, {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
        company: form.company || null
      });
      
      setSubmitted(true);
      toast.success('Registration submitted for approval!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0F1E' }}>
        <Card className="max-w-md w-full p-8 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ background: 'rgba(34,197,94,0.15)', border: '2px solid rgba(34,197,94,0.3)' }}>
            <CheckCircle size={32} className="text-green-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-white mb-3">Registration Submitted!</h2>
          <p className="text-slate-400 mb-6">
            Your coach account is pending super admin approval. You'll receive an email once approved and can then access all FABG member organizations.
          </p>
          <Link to="/login">
            <Button className="w-full" variant="outline" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              Back to Login
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#0A0F1E' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col flex-1 relative overflow-hidden px-12 py-10">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(800px circle at 12% 8%, rgba(30,64,175,0.2), rgba(10,15,30,0) 55%)'
        }} />
        
        <div className="relative z-10 flex flex-col justify-center h-full max-w-md">
          <Logo size={38} showText light />
          
          <div className="mt-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
              style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}>
              <UserCheck size={14} className="text-purple-400" />
              <span className="text-xs font-semibold" style={{ color: '#C4B5FD' }}>Coach Access Portal</span>
            </div>
            
            <h1 className="font-heading text-4xl font-bold text-white leading-tight mb-3" style={{ letterSpacing: '-0.02em' }}>
              Help FABG members<br />
              <span style={{ color: '#A78BFA' }}>maximize ROI.</span>
            </h1>
            
            <p className="text-base mb-8 leading-relaxed" style={{ color: 'rgba(148,163,184,0.85)' }}>
              Register as a Full Arch Buying Group coach to access member dashboards and provide expert guidance.
            </p>
            
            <div className="space-y-3 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-start gap-2">
                <CheckCircle size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white">Access All FABG Organizations</p>
                  <p className="text-xs text-slate-400">Automatically assigned after approval</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white">"Sign In As" Capability</p>
                  <p className="text-xs text-slate-400">Impersonate accounts to provide hands-on help</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white">Full Audit Trail</p>
                  <p className="text-xs text-slate-400">All actions logged for transparency</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

      {/* Right Panel - Form */}
      <div className="flex-1 lg:max-w-[480px] flex flex-col justify-center px-8 py-12"
        style={{ background: '#0D1526' }}>
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-8"><Logo size={36} showText light /></div>
          
          <h2 className="font-heading text-2xl font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>
            Coach Registration
          </h2>
          <p className="text-sm mb-7" style={{ color: 'rgba(148,163,184,0.7)' }}>
            Submit for super admin approval
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Full Name *</Label>
              <Input value={form.full_name} onChange={e => setField('full_name', e.target.value)}
                placeholder="Jonathan Theis" style={inputStyle} className="h-11" />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email Address *</Label>
              <Input value={form.email} onChange={e => setField('email', e.target.value)}
                type="email" placeholder="you@company.com" style={inputStyle} className="h-11" />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Phone</Label>
              <Input value={form.phone} onChange={e => setField('phone', e.target.value)}
                type="tel" placeholder="(555) 000-0000" style={inputStyle} className="h-11" />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Company/Organization</Label>
              <Input value={form.company} onChange={e => setField('company', e.target.value)}
                placeholder="Full Arch Buying Group" style={inputStyle} className="h-11" />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Password *</Label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} value={form.password}
                    onChange={e => setField('password', e.target.value)}
                    placeholder="Min 6 chars" style={inputStyle} className="h-11 pr-9" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                    onClick={() => setShowPw(!showPw)}>
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Confirm *</Label>
                <Input type="password" value={form.confirm_password}
                  onChange={e => setField('confirm_password', e.target.value)}
                  placeholder="Repeat" style={inputStyle} className="h-11" />
              </div>
            </div>
            
            <Button type="submit" className="w-full h-11 gap-2 font-semibold mt-6" disabled={loading}
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', color: 'white' }}>
              {loading ? <><Loader2 size={15} className="animate-spin" /> Submitting...</> : <>Submit for Approval</>}
            </Button>
          </form>
          
          <p className="text-center mt-6 text-sm" style={{ color: 'rgba(148,163,184,0.5)' }}>
            Already approved?{' '}
            <Link to="/login" className="text-purple-400 font-semibold hover:text-purple-300">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
